if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();
        this.init();
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };

        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type; // optional
        this.duration = this.dataset.duration; // e.g. "midnight_48h"

        // Loop by default for this mode
        this.loop = this.duration === 'midnight_48h';
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        // Decide target time
        if (this.type === 'fixed_time') {
          const countdownData = this.dataset.dueDate;
          if (countdownData) {
            this.targetTime = new Date(countdownData).getTime();
          }
        } else {
          this.targetTime = this.getEvergreenDueDate().getTime();
        }

        if (!this.targetTime) return;

        this.startTime = Date.now();

        if (this.targetTime > this.startTime) {
          this.fetchDomElements();
          this.beginCountdown();
        } else {
          const wrapper = document.querySelector('.custom-counterdown-timer-wrapper');
          if (wrapper) wrapper.style.display = 'none';
        }
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        clearInterval(this.intervalId);

        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();

          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);

        this.classList.remove('hidden');
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);

        // Your existing day/show-hide logic (left as-is)
        if (timeComponents.day <= 0) {
          const dayEl = this.querySelector('[data-timer-day]');
          const secEl = this.querySelector('[data-timer-second]');
          if (dayEl?.parentElement && !dayEl.parentElement.classList.contains('day-hidden')) {
            dayEl.parentElement.classList.add('day-hidden');
            secEl?.parentElement?.classList.remove('day-hidden');
          }
        } else {
          const dayEl = this.querySelector('[data-timer-day]');
          const secEl = this.querySelector('[data-timer-second]');
          if (secEl?.parentElement && !secEl.parentElement.classList.contains('day-hidden')) {
            secEl.parentElement.classList.add('day-hidden');
            dayEl?.parentElement?.classList.remove('day-hidden');
          }
        }

        this.units.forEach((unit) => {
          if (!this.elements[unit]) return;

          if (unit === 'day') {
            this.elements[unit].textContent = String(timeComponents[unit]);
          } else {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);

        if (this.loop) {
          // IMPORTANT: recompute next cycle target (otherwise loop won't work)
          this.targetTime = this.getEvergreenDueDate(true).getTime();
          this.beginCountdown();
          return;
        }

        this.classList.add('hidden');
        const wrapper = document.querySelector('.custom-counterdown-timer-wrapper');
        if (wrapper) wrapper.style.display = 'none';
      }

      /**
       * Evergreen due date logic
       * @param {boolean} forceNextCycle - when true, move to the next cycle if we're at/over the boundary
       */
      getEvergreenDueDate(forceNextCycle = false) {
        const now = new Date();

        // NEW: midnight-aligned 48h loop
        if (this.duration === 'midnight_48h') {
          // Start at today's midnight (local time)
          const start = new Date(now);
          start.setHours(0, 0, 0, 0);

          // End is +48h from start => always lands on 00:00 as well
          let end = new Date(start.getTime() + 48 * this.MS_IN_HRS);

          // If we're already past the end (or forcing next), jump forward by 48h blocks
          while (now.getTime() >= end.getTime() || forceNextCycle) {
            forceNextCycle = false;
            end = new Date(end.getTime() + 48 * this.MS_IN_HRS);
          }

          return end;
        }

        // Your existing options (unchanged)
        let intervalHrs;
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1);
            dueDate.setDate(1);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;

          case 'every_week': {
            const dayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - dayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          }

          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
          default:
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) startPoints.push(i);

        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }
    }
  );
}
